import { useEffect, useMemo, useState } from 'react';
import {
    Bar,
    CartesianGrid,
    ComposedChart,
    Line,
    ReferenceArea,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { Funnel, Info } from 'shared/icons';
import { useTokens } from '../../pages/operacoes/components/tokens';
import api from '../../services/api';
import { Clamp, CountLabel, FormatBrl, FormatDecimal1, FormatInt, FormatMoney } from '../../utils/format';
import { Pill } from '../campaigns/campaignCardShared';
import ErrorCard from '../ui/ErrorCard';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { SkeletonTable } from '../ui/Skeleton';
import StandardTable from '../ui/StandardTable';
import {
    DASH,
    DetailStat,
    ExpandCaret,
    FormatWeekLabels,
    GroupByToggle,
    IntelLoading,
    MlbLink,
    RowThumb,
    SkuChip,
} from './intelligenceShared';

/**
 * ELASTICIDADE DE PREÇO (Inteligência de Mercado) — leitura semanal de
 * preço × demanda por item, com veredito acionável do backend.
 *
 * A ordenação da lista é do SERVIDOR (provavelmente_caro primeiro, por
 * confiança) — reordenar no cliente desfaria a priorização que dá sentido à
 * aba, então TODAS as colunas são sortable:false (mesmo padrão da
 * WarehouseLpnsPage). A busca é SERVER-SIDE (param `search`: MLB, SKU ou
 * título, aplicado ANTES da paginação — mesma semântica do monitor de
 * campanhas); a busca client-side da StandardTable segue desligada porque
 * filtrar só a página carregada mentiria sobre o conjunto.
 *
 * A UNIDADE de análise é escolhida pelo toggle `group_by`: por ANÚNCIO (cada
 * MLB é uma leitura) ou por SKU (todos os anúncios do mesmo SKU viram uma
 * curva só — é assim que quem repõe estoque enxerga o produto, e um MLB
 * sozinho costuma ter poucas semanas de venda para o modelo). Toda a
 * identidade de linha corre pelo `group_key` do wire, nunca pelo item_id: no
 * modo sku o item_id vem nulo.
 */

const MONTHS_OPTIONS = [
    { value: 6, label: 'Últimos 6 meses' },
    { value: 12, label: 'Últimos 12 meses' },
    { value: 24, label: 'Últimos 24 meses' },
];

/** Veredito → rótulo + tom da Pill (danger = ação de baixar preço primeiro). */
const VERDICTS = {
    provavelmente_caro: { label: 'Provavelmente caro', tone: 'danger' },
    espaco_para_subir: { label: 'Espaço para subir', tone: 'success' },
    preco_ok: { label: 'Preço OK', tone: 'neutral' },
    inconclusivo: { label: 'Inconclusivo', tone: 'mono' },
};

const VERDICT_OPTIONS = [
    { value: 'all', label: 'Todos os vereditos' },
    { value: 'provavelmente_caro', label: 'Provavelmente caro' },
    { value: 'espaco_para_subir', label: 'Espaço para subir' },
    { value: 'preco_ok', label: 'Preço OK' },
    { value: 'inconclusivo', label: 'Inconclusivo' },
];

/** Tiers de confiança do motor (pt-BR); valor desconhecido sai cru em mono. */
const CONFIDENCE_PILLS = {
    alta: { label: 'Alta', tone: 'success' },
    media: { label: 'Média', tone: 'warning' },
    baixa: { label: 'Baixa', tone: 'mono' },
};

// Limites do wire para min_weeks (o servidor clampa igual; clampar aqui evita
// mandar um valor que volta silenciosamente diferente do que o usuário digitou).
const MIN_WEEKS_FLOOR = 4;
const MIN_WEEKS_CEIL = 26;
const MIN_WEEKS_DEFAULT = 6;

// Paginação padrão da casa (mesma da WarehouseLpnsPage).
const DEFAULT_PAGE_SIZE = 30;
const PAGE_SIZE_OPTIONS = [15, 30, 50, 100];

/** Elasticidade com sinal explícito e 2 casas ("+0,80" / "-1,50"). */
function formatElasticity(value) {
    // Guard explícito antes do Number(): Number(null) === 0 passaria no
    // isFinite e "sem modelo" viraria "0,00" (demanda perfeitamente
    // inelástica) — espelha o roundOrNull do backend.
    if (value === null || value === undefined) {
        return DASH;
    }
    const v = Number(value);
    if (!Number.isFinite(v)) {
        return DASH;
    }
    return v > 0 ? `+${FormatBrl(v)}` : FormatBrl(v);
}

/** Segunda-feira ISO seguinte a um week_start (UTC puro, mesmo motivo do FormatWeekLabels). */
function nextWeekIso(iso) {
    const date = new Date(`${String(iso).slice(0, 10)}T00:00:00Z`);
    if (Number.isNaN(date.getTime())) {
        return null;
    }
    date.setUTCDate(date.getUTCDate() + 7);
    return date.toISOString().slice(0, 10);
}

/** "R$ min – R$ max" ou '—' quando a faixa não existe (inconclusivo/sem banda). */
function formatBand(band) {
    if (!band || band.min === null || band.min === undefined || band.max === null || band.max === undefined) {
        return DASH;
    }
    return `${FormatMoney(band.min)} – ${FormatMoney(band.max)}`;
}

const ConfidencePill = ({ confidence }) => {
    if (!confidence) {
        return <span className="text-mono-400">{DASH}</span>;
    }
    const meta = CONFIDENCE_PILLS[confidence];
    return <Pill tone={meta?.tone || 'mono'}>{meta?.label || confidence}</Pill>;
};

/**
 * Gráfico semanal do item: barras de unidades (eixo esquerdo) + linha de
 * preço médio (eixo direito). Semana com `carried_price` (sem venda, preço
 * herdado de até 3 semanas atrás) ganha ponto VAZADO — é repetição do
 * passado, não observação nova, e o olho precisa distinguir. A faixa de
 * melhor receita entra sombreada (ReferenceArea no eixo de preço).
 * Tema/tooltip seguem os padrões de SparkChart/DetailPanel (tokens T).
 */
const WeeklyChart = ({ weeks, band, T }) => {
    const series = useMemo(() => {
        // Semanas de preço desconhecido (além do carry de 3) NÃO vêm no wire:
        // o motor as exclui da série. No eixo X categórico as vizinhas do
        // buraco ficariam adjacentes e a linha de preço "atravessaria" o
        // período desconhecido como se fosse contínuo — justamente nos itens
        // com longas paradas que a análise destaca. Sintetizamos aqui as
        // semanas de calendário faltantes com price:null (connectNulls=false
        // quebra a linha de verdade) e units:0, mantendo 1 slot por semana.
        const out = [];
        let prevStart = null;
        for (const week of Array.isArray(weeks) ? weeks : []) {
            const weekStart = String(week.week_start ?? '').slice(0, 10);
            if (prevStart) {
                let gap = nextWeekIso(prevStart);
                while (gap && gap < weekStart) {
                    const gapLabels = FormatWeekLabels(gap);
                    out.push({
                        weekStart: gap,
                        label: gapLabels.short,
                        fullLabel: gapLabels.full,
                        units: 0,
                        price: null,
                        revenue: 0,
                        carried: false,
                    });
                    gap = nextWeekIso(gap);
                }
            }
            const labels = FormatWeekLabels(week.week_start);
            out.push({
                weekStart: week.week_start,
                label: labels.short,
                fullLabel: labels.full,
                units: Number(week.units || 0),
                // null preservado (defensivo): semana sem preço conhecido
                // vira BURACO na linha, não zero.
                price: week.avg_price === null || week.avg_price === undefined ? null : Number(week.avg_price),
                revenue: Number(week.revenue || 0),
                carried: Boolean(week.carried_price),
            });
            prevStart = weekStart;
        }
        return out;
    }, [weeks]);

    if (series.length === 0) {
        return <p className="py-6 text-sm text-mono-500">Sem série semanal no período.</p>;
    }

    const hasBand = band && band.min !== null && band.min !== undefined && band.max !== null && band.max !== undefined;
    const unitsColor = T.accent;
    const priceColor = T.warn;

    // Ponto da linha de preço: vazado quando o preço é carregado. Recharts
    // chama para TODO ponto — semanas sem preço devolvem um <g/> vazio.
    const renderPriceDot = (props) => {
        const { cx, cy, payload, index } = props;
        if (cx === null || cx === undefined || cy === null || cy === undefined || payload.price === null) {
            return <g key={`dot-${payload?.weekStart ?? index}`} />;
        }
        if (payload.carried) {
            return (
                <circle
                    key={`dot-${payload.weekStart}`}
                    cx={cx}
                    cy={cy}
                    r={3}
                    fill={T.bg}
                    stroke={T.muted}
                    strokeWidth={1.5}
                />
            );
        }
        return <circle key={`dot-${payload.weekStart}`} cx={cx} cy={cy} r={2.5} fill={priceColor} />;
    };

    const ChartTooltip = ({ active, payload }) => {
        if (!active || !payload || payload.length === 0) {
            return null;
        }
        const p = payload[0].payload;
        return (
            <div
                style={{
                    background: T.bg,
                    border: `1px solid ${T.line}`,
                    padding: '6px 10px',
                    borderRadius: 4,
                    fontSize: 11,
                    fontFamily: T.mono,
                    color: T.ink,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    pointerEvents: 'none',
                }}
            >
                <div style={{ color: T.muted, marginBottom: 2 }}>semana de {p.fullLabel}</div>
                <div style={{ fontWeight: 600 }}>{FormatInt(p.units)} un</div>
                <div>
                    {p.price === null
                        ? 'preço desconhecido'
                        : `${FormatMoney(p.price)}${p.carried ? ' (preço carregado)' : ''}`}
                </div>
                {p.units > 0 ? <div style={{ color: T.muted }}>receita {FormatMoney(p.revenue)}</div> : null}
            </div>
        );
    };

    return (
        <div>
            <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: -8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.line} />
                    <XAxis
                        dataKey="label"
                        stroke={T.muted}
                        style={{ fontSize: 10, fontFamily: T.mono }}
                        // ~10 rótulos no máximo — série de 24 meses tem 104 semanas.
                        interval={Math.max(0, Math.floor(series.length / 10) - 1)}
                    />
                    <YAxis
                        yAxisId="units"
                        stroke={T.muted}
                        style={{ fontSize: 10, fontFamily: T.mono }}
                        width={36}
                        allowDecimals={false}
                    />
                    <YAxis
                        yAxisId="price"
                        orientation="right"
                        stroke={T.muted}
                        style={{ fontSize: 10, fontFamily: T.mono }}
                        width={48}
                        domain={['auto', 'auto']}
                        tickFormatter={(value) => FormatInt(value)}
                    />
                    {hasBand ? (
                        <ReferenceArea
                            yAxisId="price"
                            y1={Number(band.min)}
                            y2={Number(band.max)}
                            fill={T.ok}
                            fillOpacity={0.12}
                            stroke="none"
                            ifOverflow="extendDomain"
                        />
                    ) : null}
                    <Tooltip content={<ChartTooltip />} cursor={{ stroke: T.lineMid, strokeDasharray: '2 2' }} />
                    <Bar
                        yAxisId="units"
                        dataKey="units"
                        fill={unitsColor}
                        fillOpacity={0.75}
                        maxBarSize={18}
                        radius={[2, 2, 0, 0]}
                        isAnimationActive={false}
                    />
                    <Line
                        yAxisId="price"
                        type="monotone"
                        dataKey="price"
                        stroke={priceColor}
                        strokeWidth={2}
                        dot={renderPriceDot}
                        activeDot={{ r: 4, fill: priceColor, stroke: T.bg, strokeWidth: 2 }}
                        // Buracos honestos: semana sem preço conhecido não é
                        // interpolada — a linha quebra onde o dado acaba.
                        connectNulls={false}
                        isAnimationActive={false}
                    />
                </ComposedChart>
            </ResponsiveContainer>
            {/* Legenda manual: a nativa do recharts não sabe explicar o ponto
                vazado nem a faixa sombreada. */}
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-mono-500">
                <span className="inline-flex items-center gap-1.5">
                    <span
                        className="inline-block h-2.5 w-2.5 rounded-sm"
                        style={{ background: unitsColor, opacity: 0.75 }}
                    />
                    unidades/semana
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block h-0.5 w-4 rounded" style={{ background: priceColor }} />
                    preço médio
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <span
                        className="inline-block h-2.5 w-2.5 rounded-full border-2"
                        style={{ borderColor: T.muted, background: T.bg }}
                    />
                    preço carregado (sem venda na semana)
                </span>
                {hasBand ? (
                    <span className="inline-flex items-center gap-1.5">
                        <span
                            className="inline-block h-2.5 w-4 rounded-sm"
                            style={{ background: T.ok, opacity: 0.25 }}
                        />
                        faixa de melhor receita
                    </span>
                ) : null}
            </div>
        </div>
    );
};

/**
 * "Probabilidade de venda por faixa de preço": as semanas com preço conhecido
 * (incluindo as de preço carregado e as de ZERO venda — a evidência negativa
 * é o que torna a probabilidade honesta) recortadas em até 5 faixas por
 * quantis de preço, direto do wire (`price_bands`). A linha de melhor receita
 * mediana usa o MESMO tom emerald da banda sombreada do gráfico, para o olho
 * ligar tabela ↔ gráfico sem legenda extra.
 */
const PriceBandsTable = ({ bands, T }) => {
    if (!Array.isArray(bands) || bands.length === 0) {
        // O backend devolve [] quando há menos de 2 faixas distintas — sem
        // variação de preço não existe comparação honesta a mostrar.
        return <p className="text-sm text-mono-500">Preço variou pouco no período — sem faixas comparáveis.</p>;
    }
    return (
        <div>
            <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] text-sm">
                    <thead>
                        <tr className="border-b border-mono-200 text-[10px] uppercase tracking-wide text-mono-400 dark:border-mono-800">
                            <th className="py-1.5 pr-3 text-left font-semibold">Faixa de preço</th>
                            <th className="px-3 py-1.5 text-right font-semibold">Semanas</th>
                            <th className="px-3 py-1.5 text-left font-semibold">% semanas c/ venda</th>
                            <th className="px-3 py-1.5 text-right font-semibold">Un/semana</th>
                            <th className="py-1.5 pl-3 text-right font-semibold">Receita mediana/sem.</th>
                        </tr>
                    </thead>
                    <tbody className="text-black dark:text-white">
                        {bands.map((band, index) => {
                            const isBest = Boolean(band.is_best_revenue);
                            // Largura da barra: clamp defensivo — um wire fora
                            // de 0..100 estouraria o trilho visualmente.
                            const probability = Clamp(Number(band.sale_probability) || 0, 0, 100);
                            return (
                                <tr
                                    // Índice na key como defesa: duas faixas com os mesmos
                                    // limites em 2 casas colidiriam e o React avisaria.
                                    key={`${index}-${band.price_min}-${band.price_max}`}
                                    className={`border-b border-mono-100 last:border-0 dark:border-mono-900 ${
                                        isBest ? 'bg-success-50 dark:bg-success-900/20' : ''
                                    }`}
                                >
                                    <td className="whitespace-nowrap py-2 pr-3">
                                        <span className="tabular-nums">
                                            {FormatMoney(band.price_min)} – {FormatMoney(band.price_max)}
                                        </span>
                                        {isBest ? (
                                            <span className="ml-2 inline-block align-middle">
                                                <Pill tone="success">melhor receita</Pill>
                                            </span>
                                        ) : null}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">{FormatInt(band.weeks)}</td>
                                    <td className="px-3 py-2">
                                        <div className="flex items-center gap-2">
                                            {/* Barra proporcional em div puro (sem lib):
                                                trilho mono + preenchimento no accent do
                                                tema — mesma cor das barras de unidades
                                                do gráfico acima. */}
                                            <div className="h-1.5 w-24 shrink-0 overflow-hidden rounded-full bg-mono-100 dark:bg-mono-800">
                                                <div
                                                    className="h-full rounded-full"
                                                    style={{ width: `${probability}%`, background: T.accent }}
                                                />
                                            </div>
                                            <span className="tabular-nums">
                                                {FormatDecimal1(band.sale_probability)}%
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">
                                        {FormatDecimal1(band.avg_units_per_week)}
                                    </td>
                                    <td className="py-2 pl-3 text-right tabular-nums">
                                        {FormatMoney(band.median_weekly_revenue)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <p className="mt-2 text-[11px] text-mono-500">
                Leitura: das semanas em que o preço ficou em cada faixa (contando as sem venda), a % mostra quantas
                tiveram ao menos uma venda.
            </p>
        </div>
    );
};

/**
 * Detalhe INLINE do grupo aberto — vive dentro da linha expandida da tabela,
 * logo abaixo da linha clicada (mesma leitura dos painéis expansíveis do
 * monitor de anúncios). Os NÚMEROS (veredito, stats de preço, cobertura) vêm
 * da LINHA da lista — mesma janela `months`, sem segundo fetch para repetir o
 * que já chegou; o fetch do /:key traz só o que a lista não tem: a série
 * semanal completa do gráfico, as faixas de probabilidade de venda e, no modo
 * sku, os anúncios que compõem o grupo.
 *
 * Sem cabeçalho próprio (thumb/título/MLB/veredito): tudo isso está na linha
 * imediatamente acima — repetir ali roubaria a altura do gráfico. Fechar é o
 * mesmo clique que abriu, na linha.
 */
const ElasticityDetail = ({ row, months, groupBy }) => {
    const T = useTokens();
    const [detail, setDetail] = useState({
        loading: true,
        error: null,
        weeks: [],
        band: null,
        bands: [],
        listings: [],
    });
    // Retry local: o erro é do fetch DESTE detalhe — refazer a lista inteira
    // (e fechar a linha) seria uma resposta grande demais para a falha.
    const [retryTick, setRetryTick] = useState(0);

    const groupKey = row?.group_key;
    useEffect(() => {
        if (!groupKey) {
            return undefined;
        }
        let cancelled = false;
        setDetail({ loading: true, error: null, weeks: [], band: null, bands: [], listings: [] });
        api.getMarketIntelligencePriceElasticityItem(groupKey, { months, group_by: groupBy })
            .then((payload) => {
                if (!cancelled) {
                    setDetail({
                        loading: false,
                        error: null,
                        weeks: Array.isArray(payload?.weeks) ? payload.weeks : [],
                        band: payload?.best_revenue_band ?? null,
                        bands: Array.isArray(payload?.price_bands) ? payload.price_bands : [],
                        listings: Array.isArray(payload?.listings) ? payload.listings : [],
                    });
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setDetail({
                        loading: false,
                        error: err?.message || 'Não foi possível carregar a série semanal.',
                        weeks: [],
                        band: null,
                        bands: [],
                        listings: [],
                    });
                }
            });
        return () => {
            cancelled = true;
        };
    }, [groupKey, months, groupBy, retryTick]);

    if (!row) {
        return null;
    }
    const streak = Number(row.zero_sales_streak_weeks || 0);

    return (
        <div>
            {/* rationale inteiro: na coluna Diagnóstico ele vive truncado, e é
                aqui que a pessoa veio ler o porquê do veredito. */}
            {row.rationale ? <p className="text-xs text-mono-500">{row.rationale}</p> : null}

            <div className="mt-3 flex flex-wrap gap-x-8 gap-y-3 border-y border-mono-200 py-3 dark:border-mono-800">
                <DetailStat
                    label="Elasticidade"
                    value={row.verdict === 'inconclusivo' ? DASH : formatElasticity(row.elasticity)}
                    sub={
                        // Comparação com null, não isFinite(Number(...)):
                        // 'provavelmente_caro' pela via sem regressão chega
                        // com r2=null e Number(null)=0 mostraria "R² 0,00".
                        row.r2 !== null && row.r2 !== undefined && row.verdict !== 'inconclusivo'
                            ? `R² ${FormatBrl(row.r2)}`
                            : null
                    }
                />
                <DetailStat label="Confiança" value={<ConfidencePill confidence={row.confidence} />} />
                <DetailStat label="Preço atual" value={FormatMoney(row.current_price)} />
                <DetailStat
                    label="Preço praticado"
                    value={`${FormatMoney(row.price_min)} – ${FormatMoney(row.price_max)}`}
                    sub={`P25–P75: ${FormatMoney(row.price_p25)} – ${FormatMoney(row.price_p75)}`}
                />
                <DetailStat label="Melhor faixa (receita)" value={formatBand(row.best_revenue_band)} />
                <DetailStat label="Semanas c/ venda" value={FormatInt(row.weeks_with_sales)} />
                <DetailStat label="Sem venda há" value={streak > 0 ? `${FormatInt(streak)} sem.` : DASH} />
            </div>

            {/* Estados do detalhe ficam DENTRO da linha expandida: a tabela e a
                linha aberta continuam na tela enquanto o gráfico carrega ou
                falha — nada pisca fora do lugar onde a pessoa clicou. */}
            <div className="mt-4">
                {detail.loading ? <IntelLoading text="Carregando série semanal…" /> : null}
                {detail.error ? (
                    <div className="flex flex-wrap items-center gap-3 py-4">
                        <p className="text-sm text-danger-600 dark:text-danger-400">{detail.error}</p>
                        <button
                            type="button"
                            onClick={() => setRetryTick((tick) => tick + 1)}
                            className="rounded-lg border border-mono-300 px-3 py-1.5 text-sm text-mono-700 transition-colors hover:bg-mono-100 dark:border-mono-700 dark:text-mono-300 dark:hover:bg-mono-900"
                        >
                            Tentar novamente
                        </button>
                    </div>
                ) : null}
                {!detail.loading && !detail.error ? (
                    <>
                        {/* Faixa da resposta do detalhe (mesma janela); fallback na
                            da linha só se o detalhe não trouxe. */}
                        <WeeklyChart weeks={detail.weeks} band={detail.band ?? row.best_revenue_band} T={T} />
                        <div className="mt-4 border-t border-mono-200 pt-3 dark:border-mono-800">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-mono-400">
                                Probabilidade de venda por faixa de preço
                            </p>
                            <div className="mt-2">
                                <PriceBandsTable bands={detail.bands} T={T} />
                            </div>
                        </div>
                        {/* Só no modo sku: a curva acima é a soma de vários
                            anúncios, e sem esta lista não dá para saber QUAIS
                            anúncios entraram nem qual deles carrega a venda. */}
                        {groupBy === 'sku' ? (
                            <div className="mt-4 border-t border-mono-200 pt-3 dark:border-mono-800">
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-mono-400">
                                    Anúncios deste SKU
                                </p>
                                {detail.listings.length === 0 ? (
                                    <p className="mt-2 text-sm text-mono-500">Nenhum anúncio detalhado no período.</p>
                                ) : (
                                    <ul className="mt-2 space-y-1.5">
                                        {detail.listings.map((listing) => (
                                            <li
                                                key={listing.item_id}
                                                className="flex flex-wrap items-center gap-2 text-sm"
                                            >
                                                <MlbLink itemId={listing.item_id} />
                                                <span
                                                    className="min-w-0 flex-1 truncate text-mono-700 dark:text-mono-300"
                                                    title={listing.title || ''}
                                                >
                                                    {listing.title || DASH}
                                                </span>
                                                <span className="shrink-0 tabular-nums text-mono-500">
                                                    {FormatInt(listing.units)} un
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        ) : null}
                    </>
                ) : null}
            </div>
        </div>
    );
};

/**
 * Colunas independentes do modo — a de identificação é montada em
 * `buildColumns` porque muda com `group_by` e precisa saber qual linha está
 * aberta para girar o caret.
 */
const METRIC_COLUMNS = [
    {
        key: 'current_price',
        label: 'Preço atual',
        align: 'right',
        sortable: false,
        searchable: false,
        // Sort desligado: o getValue só alimenta o export CSV — um sentinel
        // numérico (-Infinity) sairia literal no arquivo para item sem preço.
        getValue: (row) =>
            row.current_price === null || row.current_price === undefined ? '' : Number(row.current_price),
        render: (row) => (
            <div>
                <span className="tabular-nums">{FormatMoney(row.current_price)}</span>
                {row.sale_price_amount !== null &&
                row.sale_price_amount !== undefined &&
                Number(row.sale_price_amount) !== Number(row.current_price) ? (
                    <p className="mt-0.5 text-xs tabular-nums text-mono-500">
                        promo {FormatMoney(row.sale_price_amount)}
                    </p>
                ) : null}
            </div>
        ),
    },
    {
        key: 'verdict',
        label: 'Diagnóstico',
        sortable: false,
        getValue: (row) => VERDICTS[row.verdict]?.label || row.verdict || '',
        render: (row) => {
            const verdict = VERDICTS[row.verdict];
            return (
                <div className="min-w-0">
                    <Pill tone={verdict?.tone || 'mono'}>{verdict?.label || row.verdict || DASH}</Pill>
                    {/* rationale truncado + title: a linha secundária mostra o
                        porquê sem estourar a célula; hover dá o texto inteiro. */}
                    {row.rationale ? (
                        <p
                            className="mt-1 max-w-[300px] truncate text-xs text-mono-500 2xl:max-w-[460px]"
                            title={row.rationale}
                        >
                            {row.rationale}
                        </p>
                    ) : null}
                </div>
            );
        },
    },
    {
        key: 'elasticity',
        label: 'Elasticidade',
        align: 'right',
        sortable: false,
        searchable: false,
        // Sort desligado: o único consumidor do getValue é o export CSV, e
        // Number(null)=0 exportaria "0" para toda linha sem modelo.
        getValue: (row) => (row.elasticity === null || row.elasticity === undefined ? '' : Number(row.elasticity)),
        render: (row) => {
            if (row.verdict === 'inconclusivo' || row.elasticity === null || row.elasticity === undefined) {
                return <span className="text-mono-400">{DASH}</span>;
            }
            return (
                <div>
                    <span className="font-semibold tabular-nums">{formatElasticity(row.elasticity)}</span>
                    {Number.isFinite(Number(row.r2)) ? (
                        <p className="mt-0.5 text-xs tabular-nums text-mono-500">R² {FormatBrl(row.r2)}</p>
                    ) : null}
                </div>
            );
        },
    },
    {
        key: 'confidence',
        label: 'Confiança',
        align: 'center',
        sortable: false,
        getValue: (row) => CONFIDENCE_PILLS[row.confidence]?.label || row.confidence || '',
        render: (row) => <ConfidencePill confidence={row.confidence} />,
    },
    {
        key: 'best_revenue_band',
        label: 'Melhor faixa (receita)',
        align: 'right',
        sortable: false,
        searchable: false,
        getValue: (row) =>
            row.best_revenue_band ? `${row.best_revenue_band.min ?? ''}-${row.best_revenue_band.max ?? ''}` : '',
        render: (row) => <span className="tabular-nums text-sm">{formatBand(row.best_revenue_band)}</span>,
    },
    {
        key: 'units_last_4w',
        label: 'Últimas 4 sem.',
        align: 'right',
        sortable: false,
        searchable: false,
        getValue: (row) => Number(row.units_last_4w ?? 0),
        render: (row) => (
            <div>
                <span className="tabular-nums">{FormatInt(row.units_last_4w)} un</span>
                <p className="mt-0.5 text-xs tabular-nums text-mono-500">
                    média {FormatDecimal1(row.units_weekly_avg)}/sem
                </p>
            </div>
        ),
    },
];

/**
 * Colunas da lista para o modo de agregação corrente. No modo item a
 * identidade é o anúncio (título + MLB + SKU, como sempre foi); no modo sku o
 * SKU vira o protagonista e o título só qualifica — o que importa ali é
 * quantos anúncios entraram na conta (`listings_count`), porque é isso que
 * explica por que a curva do SKU tem mais semanas que a de cada MLB.
 */
function buildColumns(groupBy, expandedKey) {
    const identityColumn =
        groupBy === 'sku'
            ? {
                  key: 'item',
                  label: 'SKU',
                  sortable: false,
                  getValue: (row) => `${row.sku || ''} ${row.title || ''}`.trim(),
                  render: (row) => (
                      <div className="flex min-w-0 items-center gap-3">
                          <ExpandCaret open={expandedKey === row.group_key} />
                          <RowThumb src={row.thumbnail} />
                          <div className="min-w-0">
                              <p className="truncate font-mono text-sm font-semibold text-black dark:text-white">
                                  {row.sku || DASH}
                              </p>
                              <p className="mt-0.5 truncate text-xs text-mono-500" title={row.title || ''}>
                                  {CountLabel(row.listings_count, 'anúncio', 'anúncios')}
                                  {row.title ? ` · ${row.title}` : ''}
                              </p>
                          </div>
                      </div>
                  ),
              }
            : {
                  key: 'item',
                  label: 'Produto',
                  sortable: false,
                  getValue: (row) => `${row.title || ''} ${row.sku || ''}`.trim(),
                  render: (row) => (
                      <div className="flex min-w-0 items-center gap-3">
                          <ExpandCaret open={expandedKey === row.group_key} />
                          <RowThumb src={row.thumbnail} />
                          <div className="min-w-0">
                              <p
                                  className="truncate text-sm font-medium text-black dark:text-white"
                                  title={row.title || ''}
                              >
                                  {row.title || DASH}
                              </p>
                              <p className="mt-0.5 flex items-center gap-2">
                                  <MlbLink itemId={row.item_id} />
                                  {row.sku ? <SkuChip sku={row.sku} /> : null}
                              </p>
                          </div>
                      </div>
                  ),
              };
    return [identityColumn, ...METRIC_COLUMNS];
}

const PriceElasticity = () => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [skipped, setSkipped] = useState(null);
    const [months, setMonths] = useState(12);
    const [verdict, setVerdict] = useState('all');
    // 'item' espelha o default do servidor — o modo não viaja quando é o padrão
    // só porque BuildQuery omite vazio, então mandamos sempre explícito.
    const [groupBy, setGroupBy] = useState('item');
    // min_weeks digitado só vira filtro no blur/Enter (padrão Oportunidades):
    // refazer a consulta a cada tecla dispararia um request por dígito.
    const [minWeeksInput, setMinWeeksInput] = useState(String(MIN_WEEKS_DEFAULT));
    const [minWeeks, setMinWeeks] = useState(MIN_WEEKS_DEFAULT);
    // Busca server-side: mesmo par digitado/aplicado do min_weeks acima — cada
    // tecla NÃO vira request; o termo só viaja no blur/Enter.
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [loading, setLoading] = useState(true);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [error, setError] = useState(null);
    const [refreshTick, setRefreshTick] = useState(0);
    // Só a CHAVE do grupo aberto: a linha em si vem da lista corrente na hora
    // de renderizar o detalhe, então não existe cópia velha para ressincronizar.
    const [expandedKey, setExpandedKey] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const offset = (Math.max(1, page) - 1) * Math.max(1, pageSize);
                const payload = await api.getMarketIntelligencePriceElasticity({
                    months,
                    min_weeks: minWeeks,
                    verdict,
                    group_by: groupBy,
                    // Termo vazio nem viaja (BuildQuery omite '') — servidor
                    // filtra item_id/SKU/título ANTES da paginação, então
                    // total/skipped já refletem o recorte buscado.
                    search,
                    limit: pageSize,
                    offset,
                });
                if (!cancelled) {
                    const list = Array.isArray(payload?.items) ? payload.items : [];
                    setRows(list);
                    setTotal(Number(payload?.total ?? list.length));
                    setSkipped(payload?.skipped ?? null);
                    // Grupo que saiu do recorte/da página fecha: manter a chave
                    // reabriria o detalhe sozinho ao voltar para a página dele,
                    // sem ninguém ter clicado.
                    setExpandedKey((prev) => (prev && list.some((row) => row.group_key === prev) ? prev : null));
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err?.message || 'Não foi possível carregar a análise de elasticidade.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                    setHasLoadedOnce(true);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [months, verdict, groupBy, minWeeks, search, page, pageSize, refreshTick]);

    // Filtro novo volta para a primeira página — offset antigo apontaria para
    // um recorte que não existe mais.
    const changeFilter = (setter) => (value) => {
        setter(value);
        setPage(1);
    };

    // Trocar o modo troca o ESPAÇO de chaves (MLB ↔ SKU): a chave aberta não
    // existe do outro lado, então o detalhe fecha junto com o reset de página.
    const changeGroupBy = (value) => {
        if (value === groupBy) {
            return;
        }
        setGroupBy(value);
        setExpandedKey(null);
        setPage(1);
    };

    const applyMinWeeks = () => {
        // Vazio/inválido volta ao default do wire; clamp espelha o servidor.
        const parsed = Clamp(Math.trunc(Number(minWeeksInput) || MIN_WEEKS_DEFAULT), MIN_WEEKS_FLOOR, MIN_WEEKS_CEIL);
        setMinWeeksInput(String(parsed));
        if (parsed !== minWeeks) {
            setMinWeeks(parsed);
            setPage(1);
        }
    };

    const applySearch = () => {
        // Trim espelha o servidor (whitespace puro = sem filtro) e normaliza o
        // que fica visível no campo; termo igual não refaz a consulta.
        const term = searchInput.trim();
        setSearchInput(term);
        if (term !== search) {
            setSearch(term);
            setPage(1);
        }
    };

    const isSkuMode = groupBy === 'sku';
    // Depende de expandedKey porque o caret da coluna de identidade mostra o
    // estado aberto/fechado da linha.
    const columns = useMemo(() => buildColumns(groupBy, expandedKey), [groupBy, expandedKey]);

    const listContent = !hasLoadedOnce ? (
        <SkeletonTable columns={7} rows={8} />
    ) : error ? (
        <ErrorCard
            title="Erro ao carregar elasticidade"
            message={error}
            action="Tentar novamente"
            actionFn={() => setRefreshTick((tick) => tick + 1)}
        />
    ) : (
        <>
            {loading ? <IntelLoading text="Recalculando análise…" /> : null}
            <StandardTable
                rows={rows}
                columns={columns}
                // group_key é a identidade do wire nos DOIS modos (MLB ou SKU);
                // item_id vem nulo no modo sku e quebraria as keys.
                rowKey={(row) => row.group_key}
                onRowClick={(row) => setExpandedKey((prev) => (prev === row.group_key ? null : row.group_key))}
                rowClassName={(row) => (expandedKey === row.group_key ? 'bg-mono-50 dark:bg-mono-900/60' : '')}
                // Detalhe inline: só a linha aberta devolve conteúdo — as demais
                // devolvem null e não geram <tr> extra.
                renderExpandedRow={(row) =>
                    expandedKey === row.group_key ? (
                        <ElasticityDetail row={row} months={months} groupBy={groupBy} />
                    ) : null
                }
                countLabel={isSkuMode ? 'SKUs analisados' : 'itens analisados'}
                // Vazio COM busca ativa é outra história: o termo não casou —
                // sem essa distinção, "nada encontrado" viraria conselho
                // errado de afrouxar filtros que não são o problema.
                emptyMessage={
                    search
                        ? `Nenhum item encontrado para “${search}” — confira o termo ou limpe a busca.`
                        : 'Nenhum item no recorte — afrouxe os filtros ou aumente a janela de meses.'
                }
                exportFileName={isSkuMode ? 'elasticidade-preco-sku' : 'elasticidade-preco'}
                searchEnabled={false}
                sortMode="server"
                paginationMode="server"
                totalItems={total}
                currentPage={page}
                onPageChange={setPage}
                itemsPerPage={pageSize}
                onItemsPerPageChange={setPageSize}
                defaultPageSize={DEFAULT_PAGE_SIZE}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
            />
        </>
    );

    return (
        <div className="space-y-4">
            {/* O que a análise faz + LIMITES: sem este card, "provavelmente
                caro" vira ordem de baixar preço — e o número é correlação
                observacional, não experimento. */}
            <div className="flex items-start gap-2 rounded-xl border border-info-200 bg-info-50 p-4 dark:border-info-800 dark:bg-info-900/20">
                <Info size={20} className="mt-0.5 shrink-0 text-info-600 dark:text-info-400" />
                <div className="text-sm text-info-800 dark:text-info-200">
                    <p>
                        Cruza <strong>preço médio semanal × unidades vendidas</strong> do seu próprio histórico e estima
                        quanto a demanda reage a mudanças de preço, com um veredito por item. Leia com cautela:{' '}
                        <strong>correlação não é causalidade</strong> — promoções costumam coincidir com datas quentes
                        (o termo de tendência do modelo mitiga esse viés, não o elimina) e as variações do mesmo MLB são
                        agregadas numa leitura única. Use como triagem de onde testar preço, não como sentença.
                        {isSkuMode ? (
                            <>
                                {' '}
                                No modo <strong>SKU</strong>, todos os anúncios que compartilham o mesmo código entram
                                numa curva só — mais semanas de evidência, mas misturando anúncios de preço e audiência
                                diferentes.
                            </>
                        ) : null}
                    </p>
                    {skipped ? (
                        <p className="mt-2 text-xs text-info-700 dark:text-info-300">
                            Cobertura: {FormatInt(total)} {isSkuMode ? 'SKUs' : 'itens'} no recorte atual ·{' '}
                            {FormatInt(skipped.insufficient_weeks)} itens sem semanas de venda suficientes ·{' '}
                            {FormatInt(skipped.no_price_variation)} itens sem variação de preço suficiente (ambos
                            aparecem como “inconclusivo”)
                            {/* Só no modo sku: no modo item o anúncio sem SKU é
                                analisado normalmente, e o número não existiria. */}
                            {isSkuMode ? <> · {FormatInt(skipped.no_sku)} anúncios sem SKU ficaram de fora</> : null}.
                        </p>
                    ) : null}
                </div>
            </div>

            {/* Filtros: busca + janela + veredito sempre visíveis; min_weeks é
                ajuste fino de quem entende a mecânica — fica atrás do
                "Avançado". */}
            <div className="flex flex-wrap items-end gap-3">
                {/* Busca server-side (padrão do monitor de campanhas: type=search
                    + placeholder claro); aplica no blur/Enter, como o min_weeks. */}
                <div className="min-w-[220px] max-w-md flex-1">
                    <Input
                        label="Buscar"
                        type="search"
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                        onBlur={applySearch}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                applySearch();
                            }
                        }}
                        placeholder="Buscar MLB, SKU ou título..."
                    />
                </div>
                <div className="w-48">
                    <Select
                        label="Janela de análise"
                        value={months}
                        onChange={(event) => changeFilter(setMonths)(Number(event.target.value))}
                    >
                        {MONTHS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Select>
                </div>
                <div className="w-52">
                    <Select
                        label="Veredito"
                        value={verdict}
                        onChange={(event) => changeFilter(setVerdict)(event.target.value)}
                    >
                        {VERDICT_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Select>
                </div>
                <GroupByToggle value={groupBy} onChange={changeGroupBy} />
                <button
                    type="button"
                    onClick={() => setShowAdvanced((value) => !value)}
                    aria-expanded={showAdvanced}
                    className="flex items-center gap-1.5 rounded-lg border border-mono-200 bg-white px-3 py-2.5 text-sm text-mono-600 hover:border-mono-400 dark:border-mono-800 dark:bg-mono-950 dark:text-mono-400"
                >
                    <Funnel size={16} /> Avançado
                </button>
                {showAdvanced ? (
                    <div className="w-44">
                        <Input
                            label={`Mín. semanas c/ venda (${MIN_WEEKS_FLOOR}–${MIN_WEEKS_CEIL})`}
                            type="number"
                            min={MIN_WEEKS_FLOOR}
                            max={MIN_WEEKS_CEIL}
                            value={minWeeksInput}
                            onChange={(event) => setMinWeeksInput(event.target.value)}
                            onBlur={applyMinWeeks}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    applyMinWeeks();
                                }
                            }}
                        />
                    </div>
                ) : null}
            </div>

            {/* O detalhe do grupo clicado abre INLINE, na própria tabela
                (renderExpandedRow): o painel solto abaixo da lista obrigava a
                pessoa a caçar de qual linha ele falava. O <td colSpan> dá ao
                gráfico de até 104 semanas a mesma largura que o painel tinha. */}
            {listContent}
        </div>
    );
};

export default PriceElasticity;
